import { useState, useEffect, useRef } from 'react'
import { useAuth } from '../../hooks/useAuth'
import Button from '../UI/Button'
import { Menu, Bell, User, LogOut, Settings } from 'lucide-react'

export default function Header({ onMenuClick }) {
  const { user, logout } = useAuth()
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [showNotifications, setShowNotifications] = useState(false)
  const notificationRef = useRef(null)
  const notifications = [] // Empty array for now - can be populated from API later

  const handleLogout = async () => {
    await logout()
  }

  // Close notifications dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false)
      }
    }

    if (showNotifications) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showNotifications])

  return (
    <header className="bg-white shadow-sm border-b border-secondary-200">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          {/* Left side */}
          <div className="flex items-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={onMenuClick}
              className="lg:hidden mr-2"
            >
              <Menu className="h-5 w-5" />
            </Button>
            
            <div className="hidden lg:block">
              {/* <h1 className="text-xl font-semibold text-secondary-900">
                Enginuo
              </h1> */}
            </div>
          </div>

          {/* Right side */}
          <div className="flex items-center space-x-4 ml-auto">
            {/* Notifications */}
            <div className="relative" ref={notificationRef}>
              <Button 
                variant="ghost" 
                size="sm" 
                className="relative"
                onClick={() => setShowNotifications(!showNotifications)}
              >
                <Bell className="h-5 w-5" />
                {notifications.length > 0 && (
                  <span className="absolute -top-1 -right-1 h-3 w-3 bg-error-500 rounded-full"></span>
                )}
              </Button>

              {showNotifications && (
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-md shadow-lg border border-secondary-200 z-50">
                  <div className="px-4 py-3 border-b border-secondary-200">
                    <h3 className="text-sm font-semibold text-secondary-900">Notifications</h3>
                  </div>
                  <div className="max-h-96 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="px-4 py-8 text-center">
                        <Bell className="h-8 w-8 text-secondary-300 mx-auto mb-2" />
                        <p className="text-sm text-secondary-500">No notifications</p>
                      </div>
                    ) : (
                      <div className="py-1">
                        {notifications.map((notification, index) => (
                          <div
                            key={index}
                            className="px-4 py-3 hover:bg-secondary-50 border-b border-secondary-100 last:border-b-0"
                          >
                            <p className="text-sm text-secondary-900">{notification.message}</p>
                            <p className="text-xs text-secondary-500 mt-1">{notification.time}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* User menu */}
            <div className="relative">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center space-x-2"
              >
                <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
                  <User className="h-4 w-4 text-primary-600" />
                </div>
                <span className="hidden md:block text-sm font-medium text-secondary-700">
                  {user?.full_name || user?.username}
                </span>
              </Button>

              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-secondary-200 py-1 z-50">
                  <div className="px-4 py-2 border-b border-secondary-100">
                    <p className="text-sm font-medium text-secondary-900">
                      {user?.full_name}
                    </p>
                    <p className="text-xs text-secondary-500">
                      {user?.email}
                    </p>
                    <p className="text-xs text-primary-600 font-medium">
                      {user?.role}
                    </p>
                  </div>
                  
                  <a
                    href="/profile"
                    className="flex items-center px-4 py-2 text-sm text-secondary-700 hover:bg-secondary-100"
                  >
                    <Settings className="h-4 w-4 mr-2" />
                    Profile Settings
                  </a>
                  
                  <button
                    onClick={handleLogout}
                    className="flex items-center w-full px-4 py-2 text-sm text-secondary-700 hover:bg-secondary-100"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  )
}
